import { useState } from 'react';
import { api } from '../../context/AuthContext';
import { API, toFullUrl } from '../../config/api';
import { Upload, Plus, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

export function BulkAlbumDialog({ onClose, onSaved }) {
  const [rows, setRows] = useState([
    { id: 1, title: '', artist: '', cover_image: '', price: 0, is_purchasable: true, release_date: '', uploading: '' },
  ]);
  const [saving, setSaving] = useState(false);

  const addRow = () => setRows((r) => [...r, { id: Date.now(), title: '', artist: '', cover_image: '', price: 0, is_purchasable: true, release_date: '', uploading: '' }]);
  const removeRow = (id) => setRows((r) => r.length > 1 ? r.filter(x => x.id !== id) : r);
  const updateRow = (id, k, v) => setRows((r) => r.map(x => x.id === id ? { ...x, [k]: v } : x));

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post(`${API}/upload/cover`, fd);
    return data.url || data.path;
  };

  const handleCover = async (id, file) => {
    if (!file) return;
    updateRow(id, 'uploading', 'cover');
    try {
      const url = await uploadFile(file);
      updateRow(id, 'cover_image', url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      updateRow(id, 'uploading', '');
    }
  };

  const handleMultiCover = (files) => {
    if (!files || files.length === 0) return;
    const stamp = Date.now();
    const baseArtist = rows[0]?.artist || '';
    const basePrice = rows[0]?.price || 0;
    const arr = Array.from(files);
    const newRows = arr.map((f, i) => ({
      id: stamp + i,
      title: f.name.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').trim(),
      artist: baseArtist, cover_image: '', price: basePrice, is_purchasable: true, release_date: '',
      uploading: 'cover', _file: f,
    }));
    setRows((r) => {
      const filtered = r.length === 1 && !r[0].title && !r[0].cover_image ? [] : r;
      return [...filtered, ...newRows];
    });
    (async () => {
      for (const nr of newRows) {
        try {
          const url = await uploadFile(nr._file);
          setRows((r) => r.map(x => x.id === nr.id ? { ...x, cover_image: url, uploading: '' } : x));
        } catch {
          setRows((r) => r.map(x => x.id === nr.id ? { ...x, uploading: '' } : x));
        }
      }
    })();
  };

  const handleSave = async () => {
    const valid = rows.filter(r => r.title && r.artist);
    if (valid.length === 0) {
      toast.error('At least one row needs title + artist');
      return;
    }
    setSaving(true);
    let saved = 0;
    for (const r of valid) {
      try {
        await api.post(`${API}/albums`, {
          title: r.title, artist: r.artist,
          cover_image: r.cover_image,
          release_date: r.release_date || null,
          is_purchasable: !!r.is_purchasable && parseFloat(r.price) > 0,
          price: parseFloat(r.price) || 0,
        });
        saved += 1;
      } catch (err) {
        toast.error(`Failed "${r.title}": ${err?.response?.data?.detail || 'error'}`);
      }
    }
    setSaving(false);
    if (saved > 0) {
      toast.success(`Created ${saved} album${saved === 1 ? '' : 's'}`);
      onSaved();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141414] border-2 border-[#FF3B30]/40 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="bulk-album-dialog">
        <div className="sticky top-0 bg-[#141414] border-b border-[#262626] p-4 flex items-center justify-between z-10">
          <h3 className="font-cabinet font-black uppercase text-lg flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#FF3B30]" /> BULK UPLOAD ALBUMS
          </h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
            <label className="cursor-pointer underline hover:text-[#FF3B30]">
              <input type="file" multiple accept="image/*" className="hidden"
                onChange={(e) => handleMultiCover(e.target.files)} data-testid="bulk-multi-cover" />
              Pick multiple cover images
            </label>
            <span> — each becomes a row. Edit title/price/artist after.</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[#737373] border-b border-[#262626]">
                  <th className="py-2 px-1 w-12">Cover</th>
                  <th className="py-2 px-1">Title *</th>
                  <th className="py-2 px-1">Artist *</th>
                  <th className="py-2 px-1 w-20">Price</th>
                  <th className="py-2 px-1 w-16 text-center">Sell?</th>
                  <th className="py-2 px-1 w-32">Release</th>
                  <th className="py-2 px-1 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#1a1a1a]">
                    <td className="py-1 px-1">
                      <label className="block w-10 h-10 bg-[#0A0A0A] border border-[#262626] cursor-pointer hover:border-[#FF3B30] overflow-hidden">
                        {r.cover_image ? <img src={toFullUrl(r.cover_image)} alt="" className="w-full h-full object-cover" /> :
                          r.uploading === 'cover' ? <Loader2 className="w-3 h-3 animate-spin text-[#FF3B30] m-auto mt-3" /> :
                          <ImageIcon className="w-4 h-4 text-[#404040] m-auto mt-3" />
                        }
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => handleCover(r.id, e.target.files?.[0])} />
                      </label>
                    </td>
                    <td className="py-1 px-1">
                      <input value={r.title} onChange={(e) => updateRow(r.id, 'title', e.target.value)}
                        className="w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs outline-none focus:border-[#FF3B30]" />
                    </td>
                    <td className="py-1 px-1">
                      <input value={r.artist} onChange={(e) => updateRow(r.id, 'artist', e.target.value)}
                        className="w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs outline-none focus:border-[#FF3B30]" />
                    </td>
                    <td className="py-1 px-1">
                      <input type="number" step="0.01" min="0" value={r.price} onChange={(e) => updateRow(r.id, 'price', e.target.value)}
                        className="w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs font-mono outline-none focus:border-[#FF3B30]" />
                    </td>
                    <td className="py-1 px-1 text-center">
                      <input type="checkbox" checked={!!r.is_purchasable}
                        onChange={(e) => updateRow(r.id, 'is_purchasable', e.target.checked)} />
                    </td>
                    <td className="py-1 px-1">
                      <input type="date" value={r.release_date} onChange={(e) => updateRow(r.id, 'release_date', e.target.value)}
                        className="w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs outline-none focus:border-[#FF3B30]" />
                    </td>
                    <td className="py-1 px-1">
                      <button onClick={() => removeRow(r.id)} className="text-[#FF3B30] hover:text-white p-1">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <button onClick={addRow} className="text-[#FF3B30] text-xs hover:text-white flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Add row
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] text-black font-black uppercase text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
              data-testid="bulk-album-save">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : `CREATE ${rows.filter(r => r.title).length} ALBUMS`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
